import type { Lead, LeadStatus } from '@up2stay/up2promo-api-client';
import { Badge } from '@up2stay/up2promo-ui';
import { cn } from '@up2stay/up2promo-ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@up2stay/up2promo-ui';
import { ChevronDown } from 'lucide-react';

import {
  LEAD_STATUS_BADGE_CLASSES,
  LEAD_STATUS_IDS,
  isLeadStatus,
  leadStatusAccent,
  leadStatusBadgeClass,
} from '@/core/components/leads/lead-status-colors';
import { triggerSelectionHaptic } from '@/core/lib/dnd-haptics';

export { LEAD_STATUS_BADGE_CLASSES };

interface LeadStatusBadgeProps {
  status: string;
  label: string;
  className?: string;
  /** Kanban cards use a tighter chip without the status dot. */
  showDot?: boolean;
  showChevron?: boolean;
}

export function LeadStatusBadge({
  status,
  label,
  className,
  showDot = true,
  showChevron = false,
}: LeadStatusBadgeProps) {
  return (
    <Badge
      variant="outline"
      className={cn(
        'w-fit gap-1.5 border-transparent font-medium',
        leadStatusBadgeClass(status),
        className,
      )}
    >
      {showDot ? (
        <span
          className="size-1.5 shrink-0 rounded-full"
          style={{ backgroundColor: leadStatusAccent(status) }}
          aria-hidden
        />
      ) : null}
      {label}
      {showChevron ? (
        <ChevronDown
          className="size-[13px] shrink-0 text-muted-foreground"
          aria-hidden
        />
      ) : null}
    </Badge>
  );
}

type LeadStatusSelectProps = {
  lead: Lead;
  statusLabels: Record<string, string>;
  onStatusChange: (status: LeadStatus) => void;
  className?: string;
};

/** Header status control — badge chip with chevron matching LeadDetail comps. */
export function LeadStatusSelect({
  lead,
  statusLabels,
  onStatusChange,
  className,
}: LeadStatusSelectProps) {
  return (
    <Select
      value={lead.status}
      onValueChange={(value) => {
        if (value == null || !isLeadStatus(value)) return;
        void triggerSelectionHaptic();
        onStatusChange(value);
      }}
    >
      <SelectTrigger
        unstyled
        className={cn(
          // Hug the badge: the unstyled trigger is still a full-width
          // justify-between row, and its own chevron (the direct-child svg)
          // would duplicate the one inside the badge.
          'inline-flex h-auto w-auto cursor-pointer items-center justify-start border-0 bg-transparent p-0 shadow-none focus-visible:ring-0 [&>svg]:hidden',
          className,
        )}
        aria-label={statusLabels[lead.status] ?? lead.status}
      >
        <LeadStatusBadge
          status={lead.status}
          label={statusLabels[lead.status] ?? lead.status}
          showChevron
          className="h-[26px] px-2.5 text-[12.5px] font-semibold"
        />
      </SelectTrigger>
      <SelectContent>
        {LEAD_STATUS_IDS.map((status) => (
          <SelectItem key={status} value={status}>
            {statusLabels[status] ?? status}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
